import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Facebook, Menu, X, Info, MessageCircle } from "lucide-react";
import { BIZ, HIGHLIGHTS, PRODUCTS, STEPS, REVIEWS } from "./rbjewelryData";
import ConceptCTAPill from "@/components/ConceptCTAPill";

const TERRACOTTA = "#B5623A";
const CREAM = "#FBF5EE";
const INK = "#2A2018";

const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.5, ease: "easeOut" } };

export default function RbJewelrySite() {
  const [open, setOpen] = useState(false);
  const links = [["About", "#about"], ["Pieces", "#pieces"], ["Reviews", "#reviews"], ["Visit", "#visit"]];

  return (
    <div id="top" className="min-h-screen" style={{ background: CREAM, color: INK, fontFamily: "'Lora', serif" }} data-testid="rbjewelry-concept-root">
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Lora:wght@400;500;600;700&family=Nunito:wght@400;500;600;700&display=swap'); .rb-sans{font-family:'Nunito',sans-serif;}`}</style>

      <div data-testid="jewelry-disclaimer" className="bg-black text-white text-center text-xs rb-sans py-2.5">
        <span className="inline-flex items-center gap-2"><Info size={13} style={{ color: TERRACOTTA }} />Concept website created by <a href="https://mysitecare.eu/ph" target="_blank" rel="noopener noreferrer" className="underline decoration-white/40 hover:decoration-white">MySiteCare</a> — not currently operated by or affiliated with {BIZ.name}.</span>
      </div>

      <header className="sticky top-0 z-40 bg-[#FBF5EE]/95 backdrop-blur shadow-sm">
        <nav className="max-w-4xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
          <a href="#top" data-testid="rbjewelry-logo" className="text-xl">RB Jewelry</a>
          <div className="hidden md:flex items-center gap-8 rb-sans">
            {links.map(([l, h]) => <a key={h} href={h} className="text-sm font-medium" style={{ color: "#5A4636" }}>{l}</a>)}
          </div>
          <a href="#visit" data-testid="rbjewelry-header-cta" className="hidden sm:inline-flex rb-sans rounded-full px-6 py-2.5 text-sm font-semibold text-white" style={{ background: TERRACOTTA }}>Visit Us</a>
          <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="rbjewelry-menu-toggle" aria-label="Menu">{open ? <X size={24} /> : <Menu size={24} />}</button>
        </nav>
        {open && <div className="md:hidden px-5 pb-4 space-y-1 bg-[#FBF5EE] rb-sans">{links.map(([l, h]) => <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2.5 font-medium">{l}</a>)}</div>}
      </header>

      {/* HERO — intimate, small-boutique framing */}
      <section className="max-w-4xl mx-auto px-5 sm:px-8 pt-16 pb-8 text-center" data-testid="rbjewelry-hero">
        <motion.div {...fadeUp}>
          <span className="rb-sans text-xs uppercase tracking-[0.25em]" style={{ color: TERRACOTTA }}>A Smaller Ongpin Jeweler</span>
          <h1 className="mt-5 text-4xl sm:text-5xl leading-[1.15]">Patient service.<br />Honest, per-gram pricing.</h1>
          <p className="mt-5 rb-sans text-[#5A4636] max-w-lg mx-auto">A smaller Ongpin jeweler where staff take the time, explain the price openly, and never rush you to buy.</p>
          <div className="mt-8 flex flex-wrap justify-center gap-4 rb-sans">
            <a href="#pieces" data-testid="rbjewelry-hero-cta" className="rounded-full px-7 py-3 text-sm font-semibold text-white" style={{ background: TERRACOTTA }}>See Our Pieces</a>
            <a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="rbjewelry-hero-message" className="rounded-full px-7 py-3 text-sm font-semibold border" style={{ borderColor: TERRACOTTA, color: TERRACOTTA }}>Message Us</a>
          </div>
          <p className="mt-7 rb-sans text-sm" style={{ color: "#8A7360" }}>{BIZ.rating}★ Rating · {BIZ.reviewCount} Reviews</p>
        </motion.div>
      </section>

      {/* ABOUT — narrative strengths, no icon circles */}
      <section id="about" className="max-w-3xl mx-auto px-5 sm:px-8 py-14">
        <motion.p {...fadeUp} className="text-xl text-center leading-relaxed" style={{ color: "#3A2E22" }}>{BIZ.intro}</motion.p>
        <div className="mt-10 space-y-4 rb-sans">
          {HIGHLIGHTS.map((h) => (
            <motion.div key={h.title} {...fadeUp} className="flex gap-4 border-l-4 pl-4" style={{ borderColor: TERRACOTTA }}>
              <div><span className="font-semibold text-sm">{h.title}</span><p className="text-sm text-[#5A4636]">{h.text}</p></div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PIECES — honest small inventory, only 2 real products */}
      <section id="pieces" className="py-16" style={{ background: "#F3E4D6" }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="text-3xl text-center mb-4">A Small, Honest Selection</motion.h2>
          <motion.p {...fadeUp} className="rb-sans text-center text-[#6B5744] mb-12">RB keeps a small, considered inventory — ask about custom requests too.</motion.p>
          <div className="grid sm:grid-cols-2 gap-8">
            {PRODUCTS.map((p) => (
              <motion.a key={p.name} {...fadeUp} href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="rbjewelry-product-card" className="bg-white shadow-sm group">
                <div className="overflow-hidden"><img src={p.img} alt={p.name} className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                <div className="p-6 rb-sans">
                  <h3 className="text-lg" style={{ fontFamily: "'Lora',serif" }}>{p.name}</h3>
                  <p className="mt-1 text-sm text-[#6B5744]">{p.desc}</p>
                  <p className="mt-3 text-sm font-semibold" style={{ color: TERRACOTTA }}>Ask About This Piece &rarr;</p>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* STEPS */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-16 rb-sans">
        {STEPS.map((s, i) => (
          <motion.div key={s.n} {...fadeUp} className="flex gap-6 py-5 border-t" style={{ borderColor: "#E4D3C0" }}>
            <span className="text-2xl font-semibold" style={{ color: TERRACOTTA }}>{s.n}</span>
            <div><h3 className="font-semibold">{s.title}</h3><p className="text-sm text-[#6B5744]">{s.text}</p></div>
          </motion.div>
        ))}
      </section>

      {/* REVIEWS — the real trust asset */}
      <section id="reviews" className="py-16" style={{ background: INK }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="text-3xl text-center mb-12 text-white">What Customers Say</motion.h2>
          <div className="space-y-6">
            {REVIEWS.map((r) => (
              <motion.div key={r.name} {...fadeUp} className="p-6 rb-sans" style={{ background: "#3A2E22" }}>
                <p className="text-white/85 text-sm leading-relaxed">"{r.text}"</p>
                <p className="mt-3 text-xs font-semibold" style={{ color: "#D8956B" }}>{r.name} · <a href={r.sourceUrl} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">{r.source}</a></p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* VISIT */}
      <section id="visit" className="max-w-4xl mx-auto px-5 sm:px-8 py-16">
        <motion.h2 {...fadeUp} className="text-3xl text-center mb-10">Visit Us</motion.h2>
        <motion.div {...fadeUp} className="grid sm:grid-cols-2 gap-8 items-start rb-sans">
          <ul className="space-y-5">
            <li className="flex items-start gap-4"><MapPin size={20} style={{ color: TERRACOTTA }} className="mt-0.5" />{BIZ.address}</li>
            <li className="flex items-start gap-4"><Phone size={20} style={{ color: TERRACOTTA }} className="mt-0.5" /><a href={`tel:${BIZ.phone.replace(/\s/g, "")}`} className="hover:underline">{BIZ.phone}</a></li>
            <li className="flex items-start gap-4"><Facebook size={20} style={{ color: TERRACOTTA }} className="mt-0.5" /><a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="rbjewelry-facebook-link" className="hover:underline">rbjewelry813</a></li>
          </ul>
          <div className="min-h-[260px] rounded-xl overflow-hidden shadow-md"><iframe title="RB Jewelry map" src={BIZ.mapEmbed} className="w-full h-full min-h-[260px]" style={{ border: 0 }} loading="lazy" referrerPolicy="no-referrer-when-downgrade" /></div>
        </motion.div>
      </section>

      <footer className="py-8 rb-sans" style={{ background: "#F3E4D6", color: "#6B5744" }}>
        <div className="max-w-4xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row justify-between gap-3 text-xs">
          <p style={{ fontFamily: "'Lora',serif", fontSize: 16, color: INK }}>RB Jewelry</p>
          <p>This is an unsolicited concept website by MySiteCare. Not operated by or affiliated with {BIZ.name}. Confirm pricing in person.</p>
        </div>
      </footer>

      <a href={BIZ.facebook} target="_blank" rel="noopener noreferrer" data-testid="rbjewelry-sticky-contact" className="md:hidden fixed bottom-4 inset-x-4 z-50 rb-sans rounded-full px-6 py-3.5 text-center text-sm font-semibold text-white shadow-xl flex items-center justify-center gap-2" style={{ background: TERRACOTTA }}><MessageCircle size={17} /> Message RB Jewelry</a>
      <ConceptCTAPill slug="rb-jewelry-concept" />
    </div>
  );
}
